import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem,
  Select, Switch, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  fetchSoftphoneConfig,
  putSoftphoneConfig,
  testSoftphoneConfig,
} from '../../services/softphone/softphoneApi';

const ACCENT = '#2391FF';
const CARD_BG = '#1a1a1a';

interface Props {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
}

const fieldSx = {
  mb: 1.75,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0f0f0f',
    color: '#fff',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
  '& .MuiInputLabel-root': { color: '#999' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiInputBase-input': { color: '#fff' },
} as const;

const sectionSx = {
  backgroundColor: CARD_BG,
  borderRadius: 1,
  p: 1.5,
  mb: 1.5,
  border: '1px solid #333',
} as const;

interface CameraOpt {
  id: number;
  name: string;
}

const SoftphoneDiscoveryTab: React.FC<Props> = ({ isDemoUser, onDemoActionAttempt }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [cameras, setCameras] = useState<CameraOpt[]>([]);

  const [enabled, setEnabled] = useState(false);
  const [ucmHost, setUcmHost] = useState('');
  const [ucmWsUpstream, setUcmWsUpstream] = useState('');
  const [wsPublicPath, setWsPublicPath] = useState('/ucm-webrtc/');
  const [doorbellExt, setDoorbellExt] = useState('');
  const [cameraId, setCameraId] = useState<number | ''>(23);
  const [amiEnabled, setAmiEnabled] = useState(false);
  const [amiHost, setAmiHost] = useState('');
  const [amiPort, setAmiPort] = useState(7777);
  const [amiUsername, setAmiUsername] = useState('');
  const [amiPassword, setAmiPassword] = useState('');
  const [amiPasswordSet, setAmiPasswordSet] = useState(false);
  const [amiStatus, setAmiStatus] = useState('disabled');
  const [amiLastError, setAmiLastError] = useState<string | null>(null);

  const loadCameras = useCallback(async () => {
    try {
      const res = await api.get('/video/cameras');
      const list = Array.isArray(res.data) ? res.data : (res.data?.cameras || res.data?.data || []);
      setCameras(
        (list as any[])
          .map((c) => ({ id: Number(c.id), name: String(c.name || c.nombre || c.id) }))
          .filter((c) => Number.isFinite(c.id)),
      );
    } catch {
      setCameras([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [cfg] = await Promise.all([fetchSoftphoneConfig(), loadCameras()]);
      setEnabled(Boolean(cfg.enabled));
      setUcmHost(cfg.ucm_host || '');
      setUcmWsUpstream(cfg.ucm_ws_upstream || '');
      setWsPublicPath(cfg.ws_public_path || '/ucm-webrtc/');
      setDoorbellExt(cfg.doorbell_sip_extension || '');
      setCameraId(cfg.camera_id ?? 23);
      setAmiEnabled(Boolean(cfg.ami_enabled));
      setAmiHost(cfg.ami_host || '');
      setAmiPort(Number(cfg.ami_port) || 7777);
      setAmiUsername(cfg.ami_username || '');
      setAmiPassword('');
      setAmiPasswordSet(Boolean(cfg.ami_password_set));
      setAmiStatus(cfg.ami_status || 'disabled');
      setAmiLastError(cfg.ami_last_error || null);
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || t('settings.discoveryHub.softphone.errorLoad')));
    } finally {
      setLoading(false);
    }
  }, [loadCameras, t]);

  useEffect(() => { void refresh(); }, [refresh]);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const buildPayload = () => {
    const body: Parameters<typeof putSoftphoneConfig>[0] = {
      enabled,
      ucm_host: ucmHost.trim() || null,
      ucm_ws_upstream: ucmWsUpstream.trim() || null,
      ws_public_path: wsPublicPath.trim() || '/ucm-webrtc/',
      doorbell_sip_extension: doorbellExt.trim() || null,
      camera_id: cameraId === '' ? 23 : Number(cameraId),
      ami_enabled: amiEnabled,
      ami_host: amiHost.trim() || null,
      ami_port: Number(amiPort) || 7777,
      ami_username: amiUsername.trim() || null,
    };
    if (amiPassword.trim()) {
      body.ami_password = amiPassword.trim();
    }
    return body;
  };

  const handleSave = async () => {
    if (blockDemo()) return;
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await putSoftphoneConfig(buildPayload());
      setOkMsg(t('settings.discoveryHub.softphone.saved'));
      await refresh();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || t('settings.discoveryHub.softphone.errorSave')));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    if (blockDemo()) return;
    setTesting(true);
    setError(null);
    setOkMsg(null);
    try {
      await putSoftphoneConfig(buildPayload());
      const res = await testSoftphoneConfig();
      if (res.success) {
        setOkMsg(t('settings.discoveryHub.softphone.testOk', { reach: res.ucm_reachability }));
      } else {
        setError(t('settings.discoveryHub.softphone.testFail', { reach: res.ucm_reachability }));
      }
      await refresh();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || t('settings.discoveryHub.softphone.testFail', { reach: 'error' })));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ color: '#fff', maxWidth: 560, mx: 'auto' }}>
      <Box sx={sectionSx}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 0.75 }}>
          {t('settings.discoveryHub.softphone.title')}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 1.5 }}>
          {t('settings.discoveryHub.softphone.help')}
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(_, v) => setEnabled(v)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
              }}
            />
          }
          label={t('settings.discoveryHub.softphone.enabled')}
          sx={{ mb: 1.5, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
        />

        <TextField
          fullWidth
          size="small"
          label={t('settings.discoveryHub.softphone.ucmHost')}
          value={ucmHost}
          onChange={(e) => setUcmHost(e.target.value)}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.discoveryHub.softphone.ucmWsUpstream')}
          value={ucmWsUpstream}
          onChange={(e) => setUcmWsUpstream(e.target.value)}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.discoveryHub.softphone.wsPublicPath')}
          value={wsPublicPath}
          onChange={(e) => setWsPublicPath(e.target.value)}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.discoveryHub.softphone.doorbellExt')}
          value={doorbellExt}
          onChange={(e) => setDoorbellExt(e.target.value)}
          sx={fieldSx}
        />

        {cameras.length > 0 ? (
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel id="softphone-camera-label">
              {t('settings.discoveryHub.softphone.cameraId')}
            </InputLabel>
            <Select
              labelId="softphone-camera-label"
              label={t('settings.discoveryHub.softphone.cameraId')}
              value={cameraId === '' ? '' : cameraId}
              onChange={(e) => setCameraId(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}
            >
              {cameras.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name} (#{c.id})</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('settings.discoveryHub.softphone.cameraId')}
            value={cameraId}
            onChange={(e) => setCameraId(e.target.value === '' ? '' : Number(e.target.value))}
            sx={fieldSx}
          />
        )}

        <Box sx={{ ...sectionSx, mt: 1.5, mb: 0, backgroundColor: '#121212' }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 0.5 }}>
            {t('settings.discoveryHub.softphone.amiSection')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#999', mb: 1.25 }}>
            {t('settings.discoveryHub.softphone.amiHelp')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={amiEnabled}
                onChange={(_, v) => setAmiEnabled(v)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                }}
              />
            }
            label={t('settings.discoveryHub.softphone.amiEnabled')}
            sx={{ mb: 1.25, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
          />
          <TextField
            fullWidth
            size="small"
            label={t('settings.discoveryHub.softphone.amiHost')}
            value={amiHost}
            onChange={(e) => setAmiHost(e.target.value)}
            placeholder={ucmHost || '10.0.0.1'}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('settings.discoveryHub.softphone.amiPort')}
            value={amiPort}
            onChange={(e) => setAmiPort(Number(e.target.value) || 7777)}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            size="small"
            label={t('settings.discoveryHub.softphone.amiUsername')}
            value={amiUsername}
            onChange={(e) => setAmiUsername(e.target.value)}
            autoComplete="off"
            sx={fieldSx}
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label={
              amiPasswordSet
                ? t('settings.discoveryHub.softphone.amiPasswordSet')
                : t('settings.discoveryHub.softphone.amiPassword')
            }
            value={amiPassword}
            onChange={(e) => setAmiPassword(e.target.value)}
            autoComplete="new-password"
            sx={fieldSx}
          />
          <Typography sx={{ fontSize: '0.72rem', color: amiStatus === 'connected' ? '#4caf50' : '#aaa' }}>
            {t('settings.discoveryHub.softphone.amiStatus', { status: amiStatus })}
          </Typography>
          {amiLastError ? (
            <Typography sx={{ fontSize: '0.72rem', color: '#ef5350', mt: 0.5 }}>
              {t('settings.discoveryHub.softphone.amiError', { error: amiLastError })}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => void handleSave()}
            sx={{ backgroundColor: ACCENT, textTransform: 'none', fontWeight: 600 }}
          >
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.discoveryHub.softphone.save')}
          </Button>
          <Button
            variant="outlined"
            disabled={testing}
            onClick={() => void handleTest()}
            sx={{ borderColor: '#555', color: '#fff', textTransform: 'none' }}
          >
            {testing ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : t('settings.discoveryHub.softphone.test')}
          </Button>
        </Box>

        {okMsg && (
          <Typography sx={{ color: '#4caf50', fontSize: '0.8rem', mt: 1.25 }}>{okMsg}</Typography>
        )}
        {error && (
          <Typography sx={{ color: '#ef5350', fontSize: '0.8rem', mt: 1.25 }}>{error}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default SoftphoneDiscoveryTab;
